import { useState, useEffect } from "react";
import { Calendar, Clock, PartyPopper } from "lucide-react";
import { trackCTAClick } from "@/utils/analytics";

const JOTFORM_URL = "https://pci.jotform.com/form/260353574059056";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const holidays = [
  { name: "October Half Term Camp", start: new Date(2026, 9, 20), end: new Date(2026, 9, 30) },
  { name: "Christmas Camp", start: new Date(2026, 11, 16), end: new Date(2026, 11, 23) },
  { name: "February Half Term Camp", start: new Date(2027, 1, 15), end: new Date(2027, 1, 19) },
  { name: "Easter Holiday Camp", start: new Date(2027, 2, 29), end: new Date(2027, 3, 9) },
  { name: "May Half Term Camp", start: new Date(2027, 5, 1), end: new Date(2027, 5, 4) },
  { name: "Summer Holiday Camp", start: new Date(2027, 6, 14), end: new Date(2027, 7, 20) },
];

function getNextHoliday() {
  const now = new Date();
  for (const holiday of holidays) {
    const endOfDay = new Date(holiday.end);
    endOfDay.setHours(23, 59, 59, 999);
    if (now <= endOfDay) return holiday;
  }
  return null;
}

function getDaysUntil(date) {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
}

export default function CountdownBanner() {
  const [holiday, setHoliday] = useState(null);
  const [days, setDays] = useState(0);
  const [bookingOpen, setBookingOpen] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/booking-banner`)
      .then((r) => r.json())
      .then((d) => { if (d.enabled && d.holiday) setBookingOpen(d.holiday); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const next = getNextHoliday();
    if (next) {
      setHoliday(next);
      setDays(getDaysUntil(next.start));
    }

    const timer = setInterval(() => {
      const next = getNextHoliday();
      if (next) {
        setHoliday(next);
        setDays(getDaysUntil(next.start));
      }
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  if (bookingOpen) {
    return (
      <div className="fixed top-0 left-0 right-0 bg-[#D4A857] text-[#0A1A2F] py-2.5 px-4 text-center z-[60]" data-testid="countdown-banner">
        <div className="max-w-6xl mx-auto flex items-center justify-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <PartyPopper size={16} />
            <span className="font-bold text-sm font-['Montserrat'] uppercase tracking-wide" data-testid="booking-open-banner-text">
              Booking now open for {bookingOpen}!
            </span>
          </div>
          <button
            onClick={() => { trackCTAClick("Booking Open Banner — " + bookingOpen); window.open(JOTFORM_URL, "_blank"); }}
            className="bg-[#0A1A2F] text-white text-xs font-bold px-4 py-1.5 rounded hover:bg-[#0D2240] transition-colors font-['Montserrat'] uppercase tracking-wide"
            data-testid="booking-open-banner-cta"
          >
            Book Now
          </button>
        </div>
      </div>
    );
  }

  if (!holiday) return null;

  const isHappening = days <= 0;

  return (
    <div
      className="fixed top-0 left-0 right-0 bg-[#D4A857] text-[#0A1A2F] py-2.5 px-4 text-center z-[60]"
      data-testid="countdown-banner"
    >
      <div className="max-w-6xl mx-auto flex items-center justify-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {isHappening ? (
            <Clock size={16} className="animate-pulse" />
          ) : (
            <Calendar size={16} />
          )}
          <span className="font-bold text-sm font-['Montserrat'] uppercase tracking-wide">
            {isHappening
              ? `${holiday.name} is happening now!`
              : `${holiday.name} starts in ${days} day${days !== 1 ? "s" : ""}!`}
          </span>
          <span className="text-xs font-['Lato'] opacity-80">
            — Some dates already sold out
          </span>
        </div>
        <button
          onClick={() => { trackCTAClick("Countdown — " + (isHappening ? "Book Now" : "Secure Your Spot")); window.open(JOTFORM_URL, "_blank"); }}
          className="bg-[#0A1A2F] text-white text-xs font-bold px-4 py-1.5 rounded hover:bg-[#0D2240] transition-colors font-['Montserrat'] uppercase tracking-wide"
          data-testid="countdown-cta"
        >
          {isHappening ? "Book Now" : "Secure Your Spot"}
        </button>
      </div>
    </div>
  );
}
